// src/logic/helpers/getSpotsLeftForFeed.ts

import { redis } from "./redis";
import { z } from "zod";
import { FeedItemSchema } from "@bridge-applications/eh-types";

type FeedItem = z.infer<typeof FeedItemSchema>;

/**
 * Returns a map: { "<TYPE>:<id>": spotsLeft | null }
 * - Uses type-scoped keys:   eh:spots_left:<productTypeLowercased>:<id>
 * - Falls back to legacy key: eh:spots_left:<id>
 * Only applies to SURVEY and PARTNER_CAMPAIGN_CHI (others → null).
 */
export async function getSpotsLeftMapForFeed(
    items: Pick<FeedItem, "productType" | "itemId">[],
): Promise<Record<string, number | null>> {
    if (!items.length) return {};

    // Only content that has a "spots" concept
    const scoped = items.filter(
        (i) =>
            i.productType === "SURVEY" ||
            i.productType === "PARTNER_CAMPAIGN_CHI" ||
            i.productType === "PARTNER_CAMPAIGN_ACTION",
    );

    // Build both modern and legacy keys (one mget call)
    const modernKeys = scoped.map(
        (i) => `eh:spots_left:${i.productType.toLowerCase()}:${i.itemId}`,
    );
    const legacyKeys = scoped.map((i) => `eh:spots_left:${i.itemId}`);

    const raw = (await redis.mget(...modernKeys, ...legacyKeys)) as (
        | number
        | null
    )[];

    const out: Record<string, number | null> = {};
    for (let idx = 0; idx < scoped.length; idx++) {
        const item = scoped[idx];
        if (!item) continue;

        const modernVal = raw[idx];
        const legacyVal = raw[idx + scoped.length];
        const val = modernVal ?? legacyVal;
        out[`${item.productType}:${item.itemId}`] =
            val !== null ? Math.max(0, Number(val)) : null;
    }

    // Non–spots items → null
    for (const i of items) {
        const k = `${i.productType}:${i.itemId}`;
        if (!(k in out)) out[k] = null;
    }

    return out;
}
